import vendorAxios, { VENDOR_TOKEN_KEY, VENDOR_REFRESH_KEY, VENDOR_INFO_KEY } from './vendorAxios';

// Thin wrappers over the /vendor/* backend endpoints. Every backend response is
// shaped { status, message, data }; helpers unwrap and throw on status:false.

const unwrap = (res: any) => {
	const body = res?.data;
	if (body && body.status === false) {
		const err: any = new Error(body.message || 'Request failed');
		err.apiMessage = body.message;
		throw err;
	}
	return body?.data;
};

// Like unwrap, but returns { data, warning, message } so callers can surface a
// soft warning (e.g. challan amount exceeds the quotation total).
const unwrapFull = (res: any) => {
	const body = res?.data;
	if (body && body.status === false) {
		const err: any = new Error(body.message || 'Request failed');
		err.apiMessage = body.message;
		throw err;
	}
	return { data: body?.data, warning: body?.warning ?? null, message: body?.message };
};

export interface VendorInfo {
	accountId: string;
	name: string;
	email?: string;
	contact_no?: string;
}

export const vendorLogin = async (username: string, password: string): Promise<VendorInfo> => {
	const res = await vendorAxios.post('/vendor/login', { username, password });
	const data = unwrap(res);
	localStorage.setItem(VENDOR_TOKEN_KEY, data.tokens.accessToken);
	localStorage.setItem(VENDOR_REFRESH_KEY, data.tokens.refreshToken);
	localStorage.setItem(VENDOR_INFO_KEY, JSON.stringify(data.vendor));
	return data.vendor;
};

export const vendorLogout = () => {
	localStorage.removeItem(VENDOR_TOKEN_KEY);
	localStorage.removeItem(VENDOR_REFRESH_KEY);
	localStorage.removeItem(VENDOR_INFO_KEY);
};

export const getVendorInfo = (): VendorInfo | null => {
	try {
		const raw = localStorage.getItem(VENDOR_INFO_KEY);
		if (!raw || raw === 'undefined' || raw === 'null') return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
};

export const isVendorLoggedIn = () => !!localStorage.getItem(VENDOR_TOKEN_KEY);

export const getDashboard = async () => unwrap(await vendorAxios.post('/vendor/dashboard', {}));
export const getProfile = async () => unwrap(await vendorAxios.post('/vendor/profile', {}));
export const listRequisitions = async (params: any = {}) =>
	unwrap(await vendorAxios.post('/vendor/requisitions', params));
export const getRequisition = async (id: string) =>
	unwrap(await vendorAxios.post('/vendor/requisition', { _id: id }));
export const submitQuotation = async (payload: any) =>
	unwrap(await vendorAxios.post('/vendor/submitQuotation', payload));
export const listMyQuotations = async (params: any = {}) =>
	unwrap(await vendorAxios.post('/vendor/myQuotations', params));
export const getMyQuotation = async (id: string) =>
	unwrap(await vendorAxios.post('/vendor/myQuotation', { _id: id }));
export const updateQuotationDetails = async (payload: any) =>
	unwrap(await vendorAxios.post('/vendor/updateQuotationDetails', payload));
export const listPaymentTerms = async () =>
	unwrap(await vendorAxios.post('/vendor/paymentTerms', {}));
export const uploadChallan = async (payload: any) =>
	unwrapFull(await vendorAxios.post('/vendor/uploadChallan', payload));
export const uploadInvoice = async (payload: any) =>
	unwrapFull(await vendorAxios.post('/vendor/uploadInvoice', payload));
export const listMyChallans = async (params: any = {}) =>
	unwrap(await vendorAxios.post('/vendor/myChallans', params));

// File upload via the vendor-gated /vendor/uploadDoc (multipart). Returns the
// stored path string that the vendor upload endpoints expect in `files`.
export const uploadFile = async (file: File): Promise<string> => {
	const form = new FormData();
	form.append('doc', file);
	const token = localStorage.getItem(VENDOR_TOKEN_KEY);
	const res = await vendorAxios.post('/vendor/uploadDoc', form, {
		headers: {
			'Content-Type': 'multipart/form-data',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
	});
	return unwrap(res);
};
